import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from "@reduxjs/toolkit/query/react";
import { message } from "antd";
import { Mutex } from "async-mutex";
import { RootState } from "shared/store/root-state.interface";
import { validateHrWebResponse } from "./validations";
import { selectApiMode } from "shared/store/configSlice";
import { BASE_URL, TEST_URL } from "shared/config/constants";
import { refreshAccessToken } from "features/auth/api/refreshService";

const mutex = new Mutex();

/**
 * ВАЖНО:
 * - prepareHeaders не видит body, поэтому Content-Type нельзя задавать там "всегда".
 * - Для JSON запросов Content-Type мы добавим вручную в baseQueryWithReauth, если body НЕ FormData.
 * - Для FormData мы НЕ ставим Content-Type вообще (браузер сам поставит boundary).
 */
const rawBaseQuery = fetchBaseQuery({
  baseUrl: "",
  prepareHeaders: (headers) => {
    const jwtToken = window.localStorage.getItem("token");
    if (jwtToken) headers.set("Authorization", `Bearer ${jwtToken}`);
    // НЕ ставим Content-Type тут
    return headers;
  },
});

function isFormDataBody(body: unknown): body is FormData {
  return typeof FormData !== "undefined" && body instanceof FormData;
}

function withJsonContentTypeIfNeeded(args: FetchArgs): FetchArgs {
  // если пользователь сам задал headers — уважаем это
  const hasHeaders = !!args.headers;

  // fetchBaseQuery может принимать Headers | Record<string,string>
  const headers =
    args.headers instanceof Headers
      ? args.headers
      : new Headers((args.headers as Record<string, string>) || undefined);

  // если body FormData — НЕ ставим content-type
  if (isFormDataBody(args.body)) {
    // на всякий случай удалим, если где-то прилетело
    headers.delete("Content-Type");
    headers.delete("content-type");
    return { ...args, headers };
  }

  // если body есть и это НЕ FormData — ставим JSON (для старых запросов)
  if (args.body != null) {
    if (!headers.has("Content-Type") && !headers.has("content-type")) {
      headers.set("Content-Type", "application/json; charset=utf-8");
    }
  }

  return { ...args, headers };
}

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const mode = selectApiMode(state);
  const host = mode === "TEST" ? TEST_URL : BASE_URL;

  const patchArgs: FetchArgs =
    typeof args === "string"
      ? { url: host + args }
      : {
          ...args,
          url: args.url?.startsWith("http") ? args.url : host + args.url,
        };

  // ✅ вот тут фикс: корректно выставляем Content-Type только для JSON,
  // а для FormData — убираем
  const preparedArgs = withJsonContentTypeIfNeeded(patchArgs);

  await mutex.waitForUnlock();
  let result = await rawBaseQuery(preparedArgs, api, extraOptions);

  try {
    validateHrWebResponse((result as any).data);
  } catch (ex) {
    message.error(String(ex));
  }

  if (result.error && result.error.status === "FETCH_ERROR") {
    message.error("Проблема сети - Убедитесь что вы подключены к сети.");
  }

  if (result.error && result.error.status === 401) {
    if (!mutex.isLocked()) {
      const release = await mutex.acquire();
      try {
        const newAccessToken = await refreshAccessToken();

        const prevHeaders =
          preparedArgs.headers instanceof Headers
            ? preparedArgs.headers
            : new Headers(
                (preparedArgs.headers as Record<string, string>) || undefined,
              );

        prevHeaders.set("Authorization", `Bearer ${newAccessToken}`);

        const retryArgs: FetchArgs = withJsonContentTypeIfNeeded({
          ...(preparedArgs as FetchArgs),
          headers: prevHeaders,
        });

        result = await rawBaseQuery(retryArgs, api, extraOptions);
      } catch {
        localStorage.removeItem("isLoggedIn");
        localStorage.removeItem("token");
        localStorage.removeItem("refreshToken");
        message.error("Сессия истекла. Войдите снова.");
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      result = await rawBaseQuery(preparedArgs, api, extraOptions);
    }
  }

  return result;
};

const staggeredBaseQuery = retry(baseQueryWithReauth, { maxRetries: 0 });

export const agroApi = createApi({
  reducerPath: "agroApi",
  baseQuery: staggeredBaseQuery,
  endpoints: () => ({}),
  tagTypes: ["Handbook", "Services", "Handbooks", "ServiceCategory"],
  keepUnusedDataFor: 0,
});
